import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import {
  Loader2, Plus, Search, Receipt, DollarSign, Trash2, Pencil,
  Building2, Zap, Truck, ShieldCheck, Wrench, Megaphone, Paperclip,
} from 'lucide-react';
import { fmtCurrency } from '../lib/localeUtils';
import api from '../lib/api';
import { FileUploadField } from '../components/FileUploadField';

const CATEGORIES = [
  { id: 'rent', label: 'Rent', icon: Building2 },
  { id: 'utilities', label: 'Utilities', icon: Zap },
  { id: 'salaries', label: 'Salaries', icon: DollarSign },
  { id: 'supplies', label: 'Supplies', icon: Receipt },
  { id: 'maintenance', label: 'Maintenance', icon: Wrench },
  { id: 'transport', label: 'Transport', icon: Truck },
  { id: 'marketing', label: 'Marketing', icon: Megaphone },
  { id: 'insurance', label: 'Insurance', icon: ShieldCheck },
  { id: 'taxes', label: 'Taxes', icon: DollarSign },
  { id: 'vendor_payment', label: 'Vendor Payment', icon: Truck },
  { id: 'equipment', label: 'Equipment', icon: Wrench },
  { id: 'licensing', label: 'Licensing', icon: ShieldCheck },
  { id: 'miscellaneous', label: 'Other', icon: Receipt },
];

const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));
const PAYMENT_METHODS = ['cash', 'bank_transfer', 'mobile_money', 'cheque', 'card'];

const empty = {
  description: '', amount: '', category: 'miscellaneous', vendor_name: '',
  reference: '', expense_date: new Date().toISOString().slice(0, 10),
  notes: '', payment_method: 'cash', attachments: [],
};

export default function ExpensesPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ grand_total: 0, by_category: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [page, setPage] = useState(0);
  const limit = 30;

  // Form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { skip: page * limit, limit };
    if (search.trim()) params.q = search.trim();
    if (catFilter) params.category = catFilter;
    api.get('/expenses', { params })
      .then((r) => {
        setRows(r.data.expenses || []);
        setTotal(r.data.total || 0);
        setSummary(r.data.summary || { grand_total: 0, by_category: {} });
      })
      .catch(() => toast.error('Failed to load expenses'))
      .finally(() => setLoading(false));
  }, [page, search, catFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      description: e.description, amount: String(e.amount), category: e.category,
      vendor_name: e.vendor_name || '', reference: e.reference || '',
      expense_date: e.expense_date || '', notes: e.notes || '',
      payment_method: e.payment_method || 'cash',
      attachments: e.attachments || [],
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.description.trim()) { toast.error('Description required'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Valid amount required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount), attachments: (form.attachments || []).map(a => typeof a === 'string' ? a : a.file_id) };
      if (editing) {
        await api.put(`/expenses/${editing.expense_id}`, payload);
        toast.success('Expense updated');
      } else {
        await api.post('/expenses', payload);
        toast.success('Expense recorded');
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success('Expense deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const pages = Math.max(1, Math.ceil(total / limit));

  // Top category cards
  const topCats = Object.entries(summary.by_category)
    .sort(([, a], [, b]) => b.total - a.total)
    .slice(0, 5);

  return (
    <div className="space-y-5 max-w-6xl" data-testid="expenses-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Expenses</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Track operational costs and vendor payments</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5" data-testid="expense-add-btn">
          <Plus className="h-4 w-4" /> Record Expense
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="col-span-1">
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Total</p>
            <p className="text-xl font-bold mt-0.5" data-testid="expenses-grand-total">{fmtCurrency(summary.grand_total)}</p>
          </CardContent>
        </Card>
        {topCats.map(([cat, data]) => {
          const meta = CAT_MAP[cat] || CAT_MAP.miscellaneous;
          const Icon = meta.icon;
          return (
            <Card key={cat}>
              <CardContent className="p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1">
                  <Icon className="h-3 w-3" /> {meta.label}
                </p>
                <p className="text-base font-bold mt-0.5">{fmtCurrency(data.total)}</p>
                <p className="text-[10px] text-muted-foreground">{data.count} record{data.count !== 1 ? 's' : ''}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search expenses..." className="pl-9 h-9 text-sm" data-testid="expense-search" />
        </div>
        <Select value={catFilter} onValueChange={(v) => { setCatFilter(v === '__all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-40 h-9" data-testid="expense-cat-filter">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm" data-testid="expenses-empty">
          <Receipt className="h-8 w-8 mx-auto mb-2 opacity-40" />
          {search || catFilter ? 'No expenses match your filters' : 'No expenses recorded yet. Click "Record Expense" to get started.'}
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs">
              <tr>
                <th className="p-3 text-left font-medium">Date</th>
                <th className="p-3 text-left font-medium">Description</th>
                <th className="p-3 text-left font-medium">Category</th>
                <th className="p-3 text-left font-medium">Vendor</th>
                <th className="p-3 text-right font-medium">Amount</th>
                <th className="p-3 text-left font-medium">Payment</th>
                <th className="p-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const meta = CAT_MAP[e.category] || CAT_MAP.miscellaneous;
                return (
                  <tr key={e.expense_id} className="border-t" data-testid={`expense-row-${e.expense_id}`}>
                    <td className="p-3 text-xs whitespace-nowrap">{e.expense_date || '—'}</td>
                    <td className="p-3">
                      <span className="font-medium">{e.description}</span>
                      {e.reference && <span className="ml-1.5 text-[10px] text-muted-foreground">#{e.reference}</span>}
                      {(e.attachments?.length > 0) && <Paperclip className="inline-block ml-1.5 h-3 w-3 text-muted-foreground" />}
                    </td>
                    <td className="p-3"><Badge variant="outline" className="text-[10px]">{meta.label}</Badge></td>
                    <td className="p-3 text-xs">{e.vendor_name || '—'}</td>
                    <td className="p-3 text-right font-mono font-medium">{fmtCurrency(e.amount)}</td>
                    <td className="p-3 text-xs capitalize">{(e.payment_method || '').replace('_', ' ')}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(e)} data-testid={`expense-edit-${e.expense_id}`}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => remove(e.expense_id)} data-testid={`expense-delete-${e.expense_id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="text-xs text-muted-foreground">Page {page + 1} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Expense' : 'Record Expense'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Description *</Label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Electricity bill — October" data-testid="expense-field-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount *</Label>
                <Input type="number" min="0" step="0.01" value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="expense-field-amount" />
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.expense_date}
                  onChange={(e) => setForm((f) => ({ ...f, expense_date: e.target.value }))} data-testid="expense-field-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="expense-field-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Payment method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
                  <SelectTrigger data-testid="expense-field-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Vendor name</Label>
              <Input value={form.vendor_name} onChange={(e) => setForm((f) => ({ ...f, vendor_name: e.target.value }))}
                placeholder="Supplier or payee" data-testid="expense-field-vendor" />
            </div>
            <div>
              <Label className="text-xs">Reference / Invoice #</Label>
              <Input value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                placeholder="INV-2026-001" data-testid="expense-field-reference" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Additional details..." className="h-16 text-xs resize-none" data-testid="expense-field-notes" />
            </div>
            <FileUploadField
              files={form.attachments || []}
              onChange={(files) => setForm((f) => ({ ...f, attachments: files }))}
              category="receipt"
              entityType="expense"
              entityId={editing?.expense_id}
              max={5}
              testId="expense-attachment"
              label="Receipts & attachments"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="expense-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {editing ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
